import {
  captureAttributionParams,
  normalizeAttributionParam,
  type AttributionParams,
} from "@/lib/attribution";
import { captureMsclkidCookieFromBrowser } from "@/lib/microsoft-click-id";

export const FIRST_TOUCH_ATTRIBUTION_COOKIE = "hyred_attribution_first";
export const LAST_TOUCH_ATTRIBUTION_COOKIE = "hyred_attribution_last";
export const FIRST_TOUCH_ATTRIBUTION_STORAGE_KEY = "hyred.attribution.first";
export const LAST_TOUCH_ATTRIBUTION_STORAGE_KEY = "hyred.attribution.last";

const FIRST_TOUCH_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const LAST_TOUCH_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

export type StoredAttribution = AttributionParams & {
  landingPath: string | null;
  capturedAt: string;
};

export type AttributionSnapshot = {
  firstTouch: StoredAttribution | null;
  lastTouch: StoredAttribution | null;
};

export function buildAttributionFromUrl({
  searchParams,
  pathname,
  referrer,
  currentOrigin,
}: {
  searchParams: URLSearchParams;
  pathname: string;
  referrer?: string | null;
  currentOrigin?: string | null;
}): StoredAttribution | null {
  const externalReferrer = normalizeExternalReferrer(referrer, currentOrigin);
  const attribution = captureAttributionParams(searchParams, externalReferrer);

  if (!hasAttributionSignal(attribution)) {
    return null;
  }

  const query = searchParams.toString();

  return {
    ...attribution,
    landingPath: normalizeAttributionParam(query ? `${pathname}?${query}` : pathname),
    capturedAt: new Date().toISOString(),
  };
}

export function captureAttributionCookiesFromBrowser({
  searchParams,
  pathname,
  referrer,
}: {
  searchParams: URLSearchParams;
  pathname: string;
  referrer?: string | null;
}) {
  if (typeof document === "undefined") {
    return;
  }

  captureMsclkidCookieFromBrowser(searchParams);

  const attribution = buildAttributionFromUrl({
    searchParams,
    pathname,
    referrer: referrer === undefined ? document.referrer : referrer,
    currentOrigin: window.location.origin,
  });

  if (!attribution) {
    return;
  }

  const firstTouch = readStoredAttributionFromBrowser(
    FIRST_TOUCH_ATTRIBUTION_COOKIE,
  );

  if (!firstTouch) {
    writeAttributionCookie(
      FIRST_TOUCH_ATTRIBUTION_COOKIE,
      attribution,
      FIRST_TOUCH_MAX_AGE_SECONDS,
    );
    writeAttributionStorage(FIRST_TOUCH_ATTRIBUTION_STORAGE_KEY, attribution);
  }

  writeAttributionCookie(
    LAST_TOUCH_ATTRIBUTION_COOKIE,
    attribution,
    LAST_TOUCH_MAX_AGE_SECONDS,
  );
  writeAttributionStorage(LAST_TOUCH_ATTRIBUTION_STORAGE_KEY, attribution);
}

export function readAttributionSnapshotFromCookieHeader(
  cookieHeader: string | null,
): AttributionSnapshot {
  return {
    firstTouch: parseStoredAttribution(
      getCookieValue(cookieHeader, FIRST_TOUCH_ATTRIBUTION_COOKIE),
    ),
    lastTouch: parseStoredAttribution(
      getCookieValue(cookieHeader, LAST_TOUCH_ATTRIBUTION_COOKIE),
    ),
  };
}

function hasAttributionSignal(attribution: AttributionParams) {
  return Boolean(
    attribution.utmSource ||
      attribution.utmMedium ||
      attribution.utmCampaign ||
      attribution.utmContent ||
      attribution.utmTerm ||
      attribution.gclid ||
      attribution.fbclid ||
      attribution.referrer,
  );
}

function normalizeExternalReferrer(
  referrer: string | null | undefined,
  currentOrigin: string | null | undefined,
): string | null {
  const normalized = normalizeAttributionParam(referrer);
  if (!normalized) return null;

  try {
    const referrerUrl = new URL(normalized);
    if (referrerUrl.protocol !== "http:" && referrerUrl.protocol !== "https:") {
      return null;
    }

    if (currentOrigin && referrerUrl.origin === new URL(currentOrigin).origin) {
      return null;
    }

    if (
      referrerUrl.hostname === "hyred.id" ||
      referrerUrl.hostname.endsWith(".hyred.id") ||
      referrerUrl.hostname === "localhost" ||
      referrerUrl.hostname.startsWith("127.") ||
      referrerUrl.hostname === "[::1]"
    ) {
      return null;
    }

    return normalized;
  } catch {
    return null;
  }
}

function writeAttributionCookie(
  name: string,
  attribution: StoredAttribution,
  maxAgeSeconds: number,
) {
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  const value = encodeURIComponent(JSON.stringify(attribution));
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; samesite=lax${secure}`;
}

function readStoredAttributionFromBrowser(name: string) {
  if (typeof document === "undefined") {
    return null;
  }

  return parseStoredAttribution(getCookieValue(document.cookie, name));
}

function writeAttributionStorage(key: string, attribution: StoredAttribution) {
  try {
    window.localStorage.setItem(key, JSON.stringify(attribution));
  } catch {
    // Cookie persistence is canonical. Storage is only a client analytics mirror.
  }
}

function parseStoredAttribution(value: string | null): StoredAttribution | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as unknown;

    if (!isStoredAttribution(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function isStoredAttribution(value: unknown): value is StoredAttribution {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    isNullableString(candidate.utmSource) &&
    isNullableString(candidate.utmMedium) &&
    isNullableString(candidate.utmCampaign) &&
    isNullableString(candidate.utmContent) &&
    isNullableString(candidate.utmTerm) &&
    isNullableString(candidate.fbclid) &&
    isNullableString(candidate.gclid) &&
    isNullableString(candidate.referrer) &&
    isNullableString(candidate.landingPath) &&
    typeof candidate.capturedAt === "string"
  );
}

function isNullableString(value: unknown) {
  return value === null || typeof value === "string";
}

function getCookieValue(cookieHeader: string | null, key: string) {
  if (!cookieHeader) {
    return null;
  }

  const cookiePrefix = `${key}=`;
  const entries = cookieHeader.split(";");

  for (const entry of entries) {
    const trimmed = entry.trim();
    if (trimmed.startsWith(cookiePrefix)) {
      return trimmed.slice(cookiePrefix.length);
    }
  }

  return null;
}
